import React, { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import ProfessionalTemplate from '@/apps/loan-officer-profile/App';
import { ProfileData } from '@/apps/loan-officer-profile/hooks/useProfileData';
import {
  PROFILE_PREVIEW_MESSAGE_VERSION,
  PROFILE_PREVIEW_READY_TYPE,
  isProfilePreviewMessage
} from '@/shared/profilePreviewContract';
import { usePublicPageDocumentScroll } from '@/hooks/usePublicPageDocumentScroll';

const MinimalTemplate = lazy(() => import('@/apps/loan-officer-profile/templates/minimal-1/MinimalTemplate'));
const TechTemplate = lazy(() => import('@/apps/loan-officer-profile/templates/tech-1/TechTemplate'));

type TemplateComponent = React.ComponentType<{ profileData: ProfileData }>;
const TEMPLATES: Record<string, TemplateComponent> = {
  'professional-1': ProfessionalTemplate,
  'minimal-1': MinimalTemplate,
  'tech-1': TechTemplate
};

const SYNTHETIC_PROFILE_PREVIEW: ProfileData = {
  firstName: 'Jordan',
  lastName: 'Lee',
  title: 'Loan Officer',
  nmls: '000000',
  phone: '(555) 010-2026',
  email: 'jordan.lee@example.com',
  bio: 'Jordan helps borrowers understand their financing options and move through the mortgage process with clear, practical guidance. This content is synthetic and appears only in the template preview.',
  location: '123 Example Avenue, Irvine, CA 92618',
  licenseStates: ['CA', 'AZ'],
  industryStartYear: 2016,
  socialMedia: {},
  facialPhoto: undefined,
  bodyPhoto: undefined,
  mortgageSpecialties: ['Conventional loans', 'FHA loans', 'VA loans', 'First-time homebuyer programs'],
  reviews: [],
  companyInfo: {
    name: 'Example Mortgage Company',
    legalName: 'Example Mortgage Company, Inc.',
    nmls: '0000000',
    phone: '(555) 010-1000',
    email: 'hello@example.com',
    address: '123 Example Avenue, Irvine, CA 92618',
    street: '123 Example Avenue',
    city: 'Irvine',
    state: 'CA',
    zip: '92618',
    website: 'https://example.com',
    equalHousingLender: true,
    disclaimers: ['This is example disclosure text for template preview purposes only.'],
    licenses: [{ state: 'CA', licenseNumber: '0000000' }],
    complianceReady: true
  },
  slug: 'synthetic-preview',
  canonicalSlug: 'synthetic-preview',
  applicationLink: 'https://example.com/apply'
};

const TemplateLoading = () => (
  <div className="flex min-h-screen items-center justify-center bg-background">
    <div className="text-center">
      <div className="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-2 border-muted border-b-primary" />
      <p className="text-sm text-muted-foreground">Loading template…</p>
    </div>
  </div>
);

export default function TemplatePreview() {
  usePublicPageDocumentScroll();
  const { templateId } = useParams<{ templateId: string }>();
  const [searchParams] = useSearchParams();
  const [profile, setProfile] = useState(SYNTHETIC_PROFILE_PREVIEW);
  const embedded = searchParams.get('embedded') === '1';

  useEffect(() => {
    const receiveDraft = (event: MessageEvent<unknown>) => {
      const message = event.data;
      if (!templateId
        || event.origin !== window.location.origin
        || event.source !== window.parent
        || !isProfilePreviewMessage(message, templateId)) {
        return;
      }
      setProfile((current) => ({
        ...current,
        ...message.profile,
        socialMedia: { ...current.socialMedia, ...message.profile.socialMedia },
        companyInfo: { ...current.companyInfo, ...message.profile.companyInfo }
      }));
    };
    window.addEventListener('message', receiveDraft);
    if (templateId) {
      window.parent?.postMessage({
        type: PROFILE_PREVIEW_READY_TYPE,
        version: PROFILE_PREVIEW_MESSAGE_VERSION,
        templateId
      }, window.location.origin);
    }
    return () => window.removeEventListener('message', receiveDraft);
  }, [templateId]);

  const Template = useMemo(() => templateId ? TEMPLATES[templateId] : null, [templateId]);
  if (!Template) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-5 text-center">
        <div>
          <h1 className="text-2xl font-semibold">Template not found</h1>
          <p className="mt-2 text-muted-foreground">The requested template is not available.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {!embedded && (
        <div className="border-b border-warning/30 bg-warning/10 px-4 py-2 text-center text-sm text-foreground">
          <strong>Synthetic preview:</strong> names, contact details, and content on this page are examples only.
        </div>
      )}
      <Suspense fallback={<TemplateLoading />}>
        <Template profileData={profile} />
      </Suspense>
    </div>
  );
}
